import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface SystemSettings {
  id?: number;
  system_name: string;
  company_name: string;
  company_address: string;
  company_short_name: string;
  company_email?: string;
  company_mobile?: string;
  company_phone?: string;
  company_fax?: string;
  company_remark?: string;
}

interface SystemContextType {
  systemSettings: SystemSettings | null;
  loading: boolean;
  refreshSettings: () => Promise<void>;
}

const SystemContext = createContext<SystemContextType | undefined>(undefined);

export const useSystem = () => {
  const context = useContext(SystemContext);
  if (context === undefined) {
    throw new Error('useSystem must be used within a SystemProvider');
  }
  return context;
};

interface SystemProviderProps {
  children: ReactNode;
}

export const SystemProvider: React.FC<SystemProviderProps> = ({ children }) => {
  const [systemSettings, setSystemSettings] = useState<SystemSettings | null>(null);
  const [loading, setLoading] = useState(true);

  // 获取系统设置
  const fetchSystemSettings = async () => {
    try {
      const response = await fetch('/api/settings');
      const result = await response.json();
      
      if (result.success) {
        setSystemSettings(result.data);
      } else {
        console.error('获取系统设置失败:', result.message);
      }
    } catch (error) {
      console.error('获取系统设置失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 刷新系统设置
  const refreshSettings = async () => {
    setLoading(true);
    await fetchSystemSettings();
  };

  // 初始化时获取系统设置
  useEffect(() => {
    fetchSystemSettings();
  }, []);

  const value: SystemContextType = {
    systemSettings,
    loading,
    refreshSettings,
  };

  return (
    <SystemContext.Provider value={value}>
      {children}
    </SystemContext.Provider>
  );
};
